import React, { useState } from 'react';
import { Card, Form, Button, Alert, Spinner, Row, Col, Table, Badge, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlus, faMinus, faGift, faHistory, faUser, faTrophy } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function PuntiFedeltaManagement() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [clientiResults, setClientiResults] = useState([]);
    const [clientiConPunti, setClientiConPunti] = useState([]);
    const [isLoadingClienti, setIsLoadingClienti] = useState(true);
    const [loyaltyData, setLoyaltyData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Stati per i form di operazione
    const [operationType, setOperationType] = useState(''); // 'add', 'revoke', 'bonus'
    const [punti, setPunti] = useState('');
    const [descrizione, setDescrizione] = useState('');
    const [note, setNote] = useState('');
    const [bonusType, setBonusType] = useState('');

    // Carica clienti con punti all'avvio
    React.useEffect(() => {
        loadClientiConPunti();
    }, []);

    const loadClientiConPunti = async () => {
        try {
            setIsLoadingClienti(true);
            const token = localStorage.getItem('access_token');
            const filters = JSON.stringify({
                punti_fedelta: { gt: 0 }
            });
            const response = await axios.get(
                `${API_URL}/api/clienti?filters=${encodeURIComponent(filters)}&limit=50&sort=punti_fedelta:desc`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setClientiConPunti(response.data.clienti || []);
        } catch (err) {
            console.error('Error loading clients with points:', err);
        } finally {
            setIsLoadingClienti(false);
        }
    };

    const searchClienti = async (term) => {
        if (!term || term.length < 2) return;
        
        try {
            setIsSearching(true);
            const token = localStorage.getItem('access_token');
            const response = await axios.get(`${API_URL}/api/clienti?q=${encodeURIComponent(term)}&per_page=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setClientiResults(response.data.data || response.data.clienti || []);
        } catch (err) {
            console.error('Error searching clients:', err);
        } finally {
            setIsSearching(false);
        }
    };

    const selectCliente = async (cliente) => {
        setSelectedCliente(cliente);
        setClientiResults([]);
        setSearchTerm('');
        await fetchClienteLoyaltyData(cliente.id);
    };

    const deselectCliente = () => {
        setSelectedCliente(null);
        setLoyaltyData(null);
        setTransactions([]);
        loadClientiConPunti(); // Ricarica la lista
    };

    const fetchClienteLoyaltyData = async (clienteId) => {
        try {
            const token = localStorage.getItem('access_token');
            
            // Fetch loyalty status
            const statusResponse = await axios.get(
                `${API_URL}/api/punti-fedelta/clienti/${clienteId}/stato`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLoyaltyData(statusResponse.data);

            // Fetch transactions
            const transactionsResponse = await axios.get(
                `${API_URL}/api/punti-fedelta/clienti/${clienteId}/transazioni?limit=20`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setTransactions(transactionsResponse.data.transazioni || []);
        } catch (err) {
            console.error('Error fetching loyalty data:', err);
            setError('Errore nel caricamento dati fedeltà');
        }
    };

    const handleAssegnaPunti = async (e) => {
        e.preventDefault();
        if (!selectedCliente) return;

        try {
            setIsProcessing(true);
            setError('');
            const token = localStorage.getItem('access_token');

            await axios.post(
                `${API_URL}/api/punti-fedelta/assegna-manuale`,
                {
                    cliente_id: selectedCliente.id,
                    punti: parseInt(punti),
                    descrizione: descrizione,
                    note: note || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSuccess(`${punti} punti assegnati con successo!`);
            resetForm();
            await fetchClienteLoyaltyData(selectedCliente.id);
            await loadClientiConPunti(); // Aggiorna la lista
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'assegnazione');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleRevocaPunti = async (e) => {
        e.preventDefault();
        if (!selectedCliente) return;

        try {
            setIsProcessing(true);
            setError('');
            const token = localStorage.getItem('access_token');

            await axios.post(
                `${API_URL}/api/punti-fedelta/revoca-manuale`,
                {
                    cliente_id: selectedCliente.id,
                    punti: parseInt(punti),
                    descrizione: descrizione,
                    note: note || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSuccess(`${punti} punti revocati con successo!`);
            resetForm();
            await fetchClienteLoyaltyData(selectedCliente.id);
            await loadClientiConPunti(); // Aggiorna la lista
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nella revoca');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleAssegnaBonus = async (e) => {
        e.preventDefault();
        if (!selectedCliente || !bonusType) return;

        try {
            setIsProcessing(true);
            setError('');
            const token = localStorage.getItem('access_token');

            await axios.post(
                `${API_URL}/api/punti-fedelta/clienti/${selectedCliente.id}/bonus-${bonusType}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setSuccess(`Bonus ${bonusType} assegnato con successo!`);
            resetForm();
            await fetchClienteLoyaltyData(selectedCliente.id);
            await loadClientiConPunti(); // Aggiorna la lista
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'assegnazione bonus');
        } finally {
            setIsProcessing(false);
        }
    };

    const resetForm = () => {
        setOperationType('');
        setPunti('');
        setDescrizione('');
        setNote('');
        setBonusType('');
    };

    const getLevelBadge = (level) => {
        const badges = {
            bronze: <Badge bg="secondary">🥉 Bronze</Badge>,
            silver: <Badge bg="secondary">🥈 Silver</Badge>,
            gold: <Badge bg="warning" text="dark">🥇 Gold</Badge>
        };
        return badges[level] || <Badge bg="secondary">Bronze</Badge>;
    };

    // I tipi arrivano dal backend come etichette (es. "Accumulo da Ordine", "Revoca Manuale")
    const getTransactionIndex = (tipo) => {
        const t = (tipo || '').toLowerCase();
        if (['accumulo', 'bonus', 'assegnazione'].some(k => t.includes(k))) return 1;
        if (['utilizzo', 'revoca', 'scadenza', 'annullamento'].some(k => t.includes(k))) return 2;
        return 0;
    };

    const getTransactionIcon = (tipo) => ['•', '+', '-'][getTransactionIndex(tipo)];

    const getTransactionColor = (tipo) => ['secondary', 'success', 'danger'][getTransactionIndex(tipo)];

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center mb-4">
                <FontAwesomeIcon icon={faTrophy} size="2x" className="text-warning me-3" />
                <div>
                    <h1 className="mb-0">Gestione Punti Fedeltà</h1>
                    <p className="text-muted mb-0">Assegna, revoca punti e gestisci bonus clienti</p>
                </div>
            </div>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Ricerca Cliente */}
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    Ricerca Cliente
                </Card.Header>
                <Card.Body>
                    <Form.Group>
                        <Form.Control
                            type="text"
                            placeholder="Cerca per nome, cognome, email..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                searchClienti(e.target.value);
                            }}
                        />
                    </Form.Group>

                    {isSearching && (
                        <div className="text-center mt-2">
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}

                    {clientiResults.length > 0 && (
                        <div className="mt-3">
                            <h6>Risultati:</h6>
                            <div className="list-group">
                                {clientiResults.map(cliente => (
                                    <button
                                        key={cliente.id}
                                        className="list-group-item list-group-item-action"
                                        onClick={() => selectCliente(cliente)}
                                    >
                                        <div className="d-flex w-100 justify-content-between">
                                            <h6 className="mb-1">
                                                {cliente.denominazione_sociale || `${cliente.nome} ${cliente.cognome}`}
                                            </h6>
                                            <small>{cliente.punti_fedelta || 0} punti</small>
                                        </div>
                                        <p className="mb-1">{cliente.email}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Clienti con Punti */}
            {!selectedCliente && (
                <Card className="mb-4">
                    <Card.Header className="bg-success text-white">
                        <FontAwesomeIcon icon={faTrophy} className="me-2" />
                        Clienti con Punti Fedeltà
                    </Card.Header>
                    <Card.Body>
                        {isLoadingClienti ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" />
                            </div>
                        ) : clientiConPunti.length > 0 ? (
                            <div className="table-responsive">
                                <Table hover>
                                    <thead>
                                        <tr>
                                            <th>Cliente</th>
                                            <th>Email</th>
                                            <th className="text-center">Punti</th>
                                            <th className="text-center">Azioni</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {clientiConPunti.map(cliente => (
                                            <tr key={cliente.id}>
                                                <td>
                                                    <strong>
                                                        {cliente.denominazione_sociale || 
                                                         `${cliente.nome} ${cliente.cognome}`}
                                                    </strong>
                                                </td>
                                                <td>{cliente.email || '-'}</td>
                                                <td className="text-center">
                                                    <Badge bg="primary" className="px-3 py-2">
                                                        {cliente.punti_fedelta || 0}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-primary"
                                                        onClick={() => selectCliente(cliente)}
                                                    >
                                                        Gestisci
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <p className="text-muted mb-0">Nessun cliente con punti fedeltà</p>
                        )}
                    </Card.Body>
                </Card>
            )}

            {/* Cliente Selezionato + Stato Punti */}
            {selectedCliente && loyaltyData && (
                <Row>
                    <Col md={6}>
                        {/* Info Cliente e Stato Punti */}
                        <Card className="mb-4">
                            <Card.Header className="bg-info text-white">
                                <FontAwesomeIcon icon={faUser} className="me-2" />
                                Cliente Selezionato
                            </Card.Header>
                            <Card.Body>
                                <h4 className="mb-3">
                                    {selectedCliente.denominazione_sociale || 
                                     `${selectedCliente.nome} ${selectedCliente.cognome}`}
                                </h4>
                                <p><strong>Email:</strong> {selectedCliente.email}</p>
                                <p><strong>ID:</strong> {selectedCliente.id}</p>

                                <hr />

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h3 className="text-primary mb-0">{loyaltyData.saldo_punti} punti</h3>
                                    {getLevelBadge(loyaltyData.livello_fedelta)}
                                </div>

                                {loyaltyData.euro_sconto_disponibile > 0 && (
                                    <p className="text-muted">
                                        Valgono €{loyaltyData.euro_sconto_disponibile.toFixed(2)} di sconto
                                    </p>
                                )}

                                {loyaltyData.punti_prossimo_livello && (
                                    <div className="mt-3">
                                        <p className="mb-1 small text-muted">
                                            Punti necessari per prossimo livello: {loyaltyData.punti_prossimo_livello} punti
                                        </p>
                                    </div>
                                )}

                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    className="mt-3"
                                    onClick={deselectCliente}
                                >
                                    Cambia Cliente
                                </Button>
                            </Card.Body>
                        </Card>

                        {/* Form Operazioni */}
                        <Card className="mb-4">
                            <Card.Header className="bg-warning text-dark">
                                <FontAwesomeIcon icon={faGift} className="me-2" />
                                Operazioni
                            </Card.Header>
                            <Card.Body>
                                <div className="d-grid gap-2 mb-3">
                                    <Button
                                        variant={operationType === 'add' ? 'success' : 'outline-success'}
                                        onClick={() => setOperationType('add')}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                                        Assegna Punti
                                    </Button>
                                    <Button
                                        variant={operationType === 'revoke' ? 'danger' : 'outline-danger'}
                                        onClick={() => setOperationType('revoke')}
                                    >
                                        <FontAwesomeIcon icon={faMinus} className="me-2" />
                                        Revoca Punti
                                    </Button>
                                    <Button
                                        variant={operationType === 'bonus' ? 'info' : 'outline-info'}
                                        onClick={() => setOperationType('bonus')}
                                    >
                                        <FontAwesomeIcon icon={faGift} className="me-2" />
                                        Assegna Bonus
                                    </Button>
                                </div>

                                {operationType === 'add' && (
                                    <Form onSubmit={handleAssegnaPunti}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Punti da assegnare</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                required
                                                value={punti}
                                                onChange={(e) => setPunti(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Descrizione *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={descrizione}
                                                onChange={(e) => setDescrizione(e.target.value)}
                                                placeholder="Es: Bonus speciale"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Note (opzionali)</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Button variant="success" type="submit" disabled={isProcessing}>
                                            {isProcessing ? 'Elaborazione...' : 'Assegna Punti'}
                                        </Button>
                                    </Form>
                                )}

                                {operationType === 'revoke' && (
                                    <Form onSubmit={handleRevocaPunti}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Punti da revocare</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="1"
                                                max={loyaltyData.saldo_punti}
                                                required
                                                value={punti}
                                                onChange={(e) => setPunti(e.target.value)}
                                            />
                                            <Form.Text className="text-muted">
                                                Max: {loyaltyData.saldo_punti} punti
                                            </Form.Text>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Motivo *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                required
                                                value={descrizione}
                                                onChange={(e) => setDescrizione(e.target.value)}
                                                placeholder="Es: Correzione errore"
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Note (opzionali)</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={note}
                                                onChange={(e) => setNote(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Button variant="danger" type="submit" disabled={isProcessing}>
                                            {isProcessing ? 'Elaborazione...' : 'Revoca Punti'}
                                        </Button>
                                    </Form>
                                )}

                                {operationType === 'bonus' && (
                                    <Form onSubmit={handleAssegnaBonus}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tipo Bonus</Form.Label>
                                            <Form.Select
                                                required
                                                value={bonusType}
                                                onChange={(e) => setBonusType(e.target.value)}
                                            >
                                                <option value="">Seleziona tipo...</option>
                                                <option value="compleanno">🎂 Compleanno</option>
                                                <option value="recensione">⭐ Recensione</option>
                                                <option value="referral">👥 Referral</option>
                                            </Form.Select>
                                        </Form.Group>
                                        <Button variant="info" type="submit" disabled={isProcessing}>
                                            {isProcessing ? 'Elaborazione...' : 'Assegna Bonus'}
                                        </Button>
                                    </Form>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Storico Transazioni */}
                    <Col md={6}>
                        <Card>
                            <Card.Header className="bg-secondary text-white">
                                <FontAwesomeIcon icon={faHistory} className="me-2" />
                                Storico Transazioni (ultime 20)
                            </Card.Header>
                            <Card.Body style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                {transactions.length === 0 ? (
                                    <p className="text-muted">Nessuna transazione</p>
                                ) : (
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                            <tr>
                                                <th>Data</th>
                                                <th>Tipo</th>
                                                <th>Punti</th>
                                                <th>Saldo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((tx) => (
                                                <tr key={tx.id}>
                                                    <td className="small">
                                                        {new Date(tx.data_transazione).toLocaleDateString('it-IT')}
                                                    </td>
                                                    <td>
                                                        <Badge bg={getTransactionColor(tx.tipo_transazione)}>
                                                            {getTransactionIcon(tx.tipo_transazione)} {tx.tipo_transazione}
                                                        </Badge>
                                                        <div className="small text-muted">{tx.descrizione}</div>
                                                    </td>
                                                    <td className={`text-${getTransactionColor(tx.tipo_transazione)}`}>
                                                        <strong>{Math.abs(tx.punti)}</strong>
                                                    </td>
                                                    <td>{tx.saldo_nuovo}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
}
